import { useState, useRef, useEffect } from 'react';
import type { IntegrationAccount } from '@/types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAtomValue } from 'jotai';
import { useIntegrations, useDisconnectIntegration, useDisconnectGorgias, useSetPrimaryShopifyStore } from '@/hooks';
import { integrationService } from '@/services';
import type { RichpanelTagResult, RichpanelUser, ShopifyScope } from '@/services/integration.service';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Radio, MoreHorizontal, Eye, EyeOff, Copy, Info, Check, UserCheck, Tag, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { currentUserAtom } from '@/hooks/use-auth';
import type { Integration } from '../types';
import { DisconnectConfirmHost } from './disconnect-confirm';
import { confirmDisconnect } from './disconnect-confirm.store';
import { HelpScoutContent } from './helpscout-content';
import { FreshdeskContent } from './freshdesk-content';
import { GladlyContent } from './gladly-content';
import { IntercomContent } from './intercom-content';
import { MetaContent } from './meta-content';
import { KimHelpdeskContent } from './kim-helpdesk-content';
import { RechargeContent } from './recharge-content';
import { MintsoftContent } from './mintsoft-content';
import { KUSTOMER_SUBDOMAIN_PATTERN, normalizeKustomerSubdomain } from '../kustomer';

interface IntegrationSheetProps {
	integration: Integration;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
}

export const IntegrationSheet = ({ integration, isOpen, onOpenChange }: IntegrationSheetProps) => {
	const renderContent = () => {
		switch (integration.name.toLowerCase()) {
			case 'gorgias':
				return <GorgiasContent integration={integration} />;
			case 'help scout':
				return <HelpScoutContent integration={integration} />;
			case 'shopify':
				return <ShopifyContent integration={integration} />;
			case 'zoho':
				return <ZohoContent integration={integration} />;
			case 'smartrr':
				return <SmartrrContent integration={integration} />;
			case 'skio':
				return <SkioContent integration={integration} />;
			case 'smile.io':
				return <SmileContent integration={integration} />;
			case 'infoplus':
				return <InfoPlusContent integration={integration} />;
			case 'clickpost':
				return <ClickPostContent integration={integration} />;
			case 'zendesk':
				return <ZendeskContent integration={integration} />;
			case 'google':
				return <GoogleContent integration={integration} />;
			case 'judge.me':
				return <JudgemeContent integration={integration} />;
			case 'aftership':
				return <AfterShipContent integration={integration} />;
			case 'wonderment':
				return <WondermentContent integration={integration} />;
			case 'checkout champ':
				return <CheckoutChampContent integration={integration} />;
			case 'shipbob':
				return <ShipBobContent integration={integration} />;
			case 'recharge':
				return <RechargeContent integration={integration} />;
			case 'shipstation':
				return <ShipstationContent integration={integration} />;
			case 'yotpo':
				return <YotpoContent integration={integration} />;
			case 'slack':
				return <SlackContent integration={integration} />;
			case 'loop returns':
				return <LoopReturnsContent integration={integration} />;
			case 'klaviyo':
				return <KlaviyoContent integration={integration} />;
			case 'usps':
				return <USPSContent integration={integration} />;
			case 'royal mail':
				return <RoyalMailContent integration={integration} />;
			case 'reviews.io':
				return <ReviewsioContent integration={integration} />;
			case 'trustpilot':
				return <TrustpilotContent integration={integration} />;
			case 'richpanel':
				return <RichpanelContent integration={integration} onOpenChange={onOpenChange} />;
			case 'freshdesk':
				return <FreshdeskContent integration={integration} isOpen={isOpen} onOpenChange={onOpenChange} />;
			case 'kustomer':
				return <KustomerContent integration={integration} onOpenChange={onOpenChange} />;
			case 'warehance':
				return <WarehanceContent integration={integration} />;
			case 'mintsoft':
				return <MintsoftContent integration={integration} />;
			case 'gladly':
				return <GladlyContent integration={integration} onOpenChange={onOpenChange} />;
			case 'intercom':
				return <IntercomContent integration={integration} />;
			case 'meta':
				return <MetaContent integration={integration} />;
			case 'kim helpdesk':
				return <KimHelpdeskContent integration={integration} />;
			default:
				return <div>Configuration not available</div>;
		}
	};

	// Wizard/multi-step integrations get a wider surface: Richpanel, Freshdesk & Gladly by
	// name, Help Scout (and any others) via the data-driven `sheetSize: 'wide'` catalog flag.
	// Widths bumped 25% (600→750, 400→500) by the integrations-modal-width fix.
	const usesWizardWidth =
		integration.sheetSize === 'wide' ||
		['richpanel', 'freshdesk', 'gladly'].includes(integration.name.toLowerCase());
	const widthClass = usesWizardWidth ? 'max-w-[750px]' : 'max-w-[500px]';

	return (
		<Dialog open={isOpen} onOpenChange={onOpenChange}>
			<DialogContent className={`${widthClass} w-full gap-5 flex flex-col p-0 max-h-[85vh]`}>
				<DialogHeader className='flex flex-row items-center px-5 pt-5 pb-0'>
					<div className='h-[52px] w-[52px] flex items-center justify-center border py-0.5 px-0.5 rounded-lg'>
						<div className='h-11 w-11 flex items-center justify-center border p-2 rounded-md'>
							<img src={integration.logo} alt={integration.name} className='h-full w-full object-contain' />
						</div>
					</div>
					<DialogTitle className='font-semibold ml-1'>{integration.name} Integration</DialogTitle>
				</DialogHeader>
				<Separator />
				<div className='flex-1 overflow-auto'>{renderContent()}</div>
				{/* Lives here, outside every dropdown menu, so a disconnect always gets its confirmation step. */}
				<DisconnectConfirmHost />
			</DialogContent>
		</Dialog>
	);
};

const gorgiasFormSchema = z.object({
	domain: z.string().min(1, 'Domain name is required').max(253, 'Domain name is too long'),
	supportEmails: z.string().min(1, 'Support emails is required').max(1000, 'Support emails value is too long')
});

type GorgiasFormData = z.infer<typeof gorgiasFormSchema>;

interface IntegrationContentProps {
	integration: Integration;
}

// Helper to mask API key/token, showing only last 8 characters
const maskApiKey = (key: string) => {
	if (key.length <= 8) return key;
	return '•'.repeat(key.length - 8) + key.slice(-8);
};

const GorgiasContent = ({ integration }: IntegrationContentProps) => {
	const currentUser = useAtomValue(currentUserAtom);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showToken, setShowToken] = useState(false);
	const disconnectMutation = useDisconnectGorgias();
	const integrationsQuery = useIntegrations();
	const pollTimerRef = useRef<number | null>(null);

	useEffect(() => {
		return () => {
			if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
		};
	}, []);

	const form = useForm<GorgiasFormData>({
		resolver: zodResolver(gorgiasFormSchema),
		defaultValues: {
			domain: '',
			supportEmails: ''
		}
	});

	const handleConnect = (data: GorgiasFormData) => {
		setIsSubmitting(true);
		const backendUrl = import.meta.env.VITE_MACHINE_BASE_URL || 'https://api.dev.kim.cc/api';
		const accountId = currentUser?.accountId || '';
		const encodedSupportEmails = encodeURIComponent(data.supportEmails);
		const popup = window.open(
			`${backendUrl}/gorgias/oauth/install?account=${encodeURIComponent(
				data.domain
			)}&support_emails=${encodedSupportEmails}&state=${encodeURIComponent(accountId)}`,
			'_blank'
		);

		if (!popup) {
			setIsSubmitting(false);
			return;
		}

		// The OAuth flow completes in the popup tab, not this one, so poll
		// integrations until this one shows connected (or the popup is closed
		// without completing) instead of assuming success immediately.
		pollTimerRef.current = window.setInterval(async () => {
			try {
				const result = await integrationsQuery.refetch();
				const updated = result.data ?? [];
				const gorgias = updated.find((i) => i.name?.toLowerCase() === 'gorgias' && i.isConnected);
				if (gorgias || popup.closed) {
					setIsSubmitting(false);
					if (popup && !popup.closed) popup.close();
					if (pollTimerRef.current) {
						window.clearInterval(pollTimerRef.current);
						pollTimerRef.current = null;
					}
				}
			} catch {
				// continue polling on transient errors
			}
		}, 2000);
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending || !currentUser?.accountId) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(Number(currentUser.accountId));
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{integration.connectedName && (
					<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
						<Label className='text-xs text-muted-foreground'>Connected Account</Label>
						<span className='text-sm font-medium'>{integration.connectedName}</span>
					</div>
				)}

				{/* Show Gorgias app token if available */}
				{(() => {
					const meta = integration.authMetaData as Record<string, unknown> | undefined;
					const appToken = meta?.gorgias_app_token as string | undefined;
					if (!appToken) return null;
					return (
						<div className='flex flex-col gap-2 p-4 bg-muted rounded-lg'>
							<div className='flex items-center gap-2 mb-1'>
								<div className='h-5 w-5 rounded-full bg-green-500 flex items-center justify-center text-white text-xs'>✓</div>
								<Label className='text-xs text-muted-foreground'>Your App Token</Label>
							</div>
							<div className='flex items-center gap-2'>
								<code className='flex-1 bg-neutral-100 border border-neutral-200 text-neutral-800 p-2 rounded-md overflow-auto text-xs font-mono'>
									{showToken ? appToken : maskApiKey(appToken)}
								</code>
								<Button
									type='button'
									size='icon-sm'
									variant='ghost'
									onClick={() => setShowToken(!showToken)}
									title={showToken ? 'Hide token' : 'Show token'}
								>
									{showToken ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
								</Button>
								<Button
									type='button'
									size='sm'
									variant='outline'
									onClick={async () => {
										try {
											await navigator.clipboard.writeText(appToken);
											toast.success('Copied to clipboard');
										} catch (e) {
											console.warn('Clipboard write failed', e);
										}
									}}
								>
									Copy
								</Button>
							</div>
							<p className='text-xs text-muted-foreground'>Use this token in your Gorgias HTTP integration.</p>
						</div>
					);
				})()}
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your credentials to activate the integration.</p>
					</div>

					<div className='flex flex-col gap-4'>
						<FormField
							control={form.control}
							name='domain'
							render={({ field }) => (
								<FormItem>
									<FormLabel>Domain Name</FormLabel>
									<FormControl>
										<Input placeholder='{{your-store}}.gorgias.com' {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name='supportEmails'
							render={({ field }) => (
								<FormItem>
									<FormLabel>Support Emails</FormLabel>
									<FormControl>
										<Textarea placeholder='support@example.com, support2@example.com' rows={3} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						Connect
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const shopifyFormSchema = z.object({
	shop: z.string().min(1, 'Shop name is required').max(253, 'Shop name is too long')
});

type ShopifyFormData = z.infer<typeof shopifyFormSchema>;

/** 'acme-us' from whatever the merchant typed or machine stored ('acme-us', 'acme-us.myshopify.com', a URL). */
const shopifyHandle = (value: unknown): string =>
	String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/^https?:\/\//, '')
		.replace(/\.myshopify\.com.*$/, '')
		.replace(/\/.*$/, '');

const ShopifyContent = ({ integration }: IntegrationContentProps) => {
	const currentUser = useAtomValue(currentUserAtom);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [scopeCatalog, setScopeCatalog] = useState<ShopifyScope[]>([]);
	const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
	const [scopesFailed, setScopesFailed] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const setPrimaryMutation = useSetPrimaryShopifyStore();
	const integrationsQuery = useIntegrations();
	const pollTimerRef = useRef<number | null>(null);
	// One row per connected store, primary first (see mergeIntegrationData).
	const stores = (integration.instances ?? []).filter((row) => row.isConnected);

	useEffect(() => {
		return () => {
			if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		integrationService
			.getShopifyScopes()
			.then((catalog) => {
				if (cancelled) return;
				// An empty catalog (e.g. SHOPIFY_SCOPES unset) is as unusable as a
				// failed fetch — show the fallback copy instead of loading forever.
				if (catalog.length === 0) {
					setScopesFailed(true);
					return;
				}
				setScopeCatalog(catalog);
				setSelectedScopes(catalog.map((scope) => scope.value));
			})
			.catch(() => {
				// Keep the default empty selection. The backend falls back to
				// its own default scope set if none are supplied.
				if (!cancelled) setScopesFailed(true);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const form = useForm<ShopifyFormData>({
		resolver: zodResolver(shopifyFormSchema),
		defaultValues: { shop: '' }
	});

	const scopeGroups = scopeCatalog.reduce<Record<string, ShopifyScope[]>>((acc, scope) => {
		if (!acc[scope.category]) acc[scope.category] = [];
		acc[scope.category].push(scope);
		return acc;
	}, {});

	const toggleScope = (value: string) => {
		if (scopeCatalog.some((scope) => scope.value === value && scope.required)) return;
		setSelectedScopes((prev) =>
			prev.includes(value) ? prev.filter((scope) => scope !== value) : [...prev, value]
		);
	};

	// Two presets only: everything Kim asks for, or reads without writes. ('All'
	// and 'Read & write' were the same full catalog — one button, one meaning.)
	const scopePresets = [
		{
			label: 'Recommended',
			apply: () => scopeCatalog.map((scope) => scope.value)
		},
		{
			label: 'Read only',
			apply: () =>
				scopeCatalog
					.filter((scope) => scope.required || !scope.value.startsWith('write'))
					.map((scope) => scope.value)
		}
	];

	const handleConnect = async (data: ShopifyFormData) => {
		if (scopeCatalog.length === 0 && !scopesFailed) {
			toast.error('Still loading the permission list — try again in a moment.');
			return;
		}
		if (scopeCatalog.length > 0 && selectedScopes.length === 0) {
			toast.error('Select at least one Shopify permission to connect.');
			return;
		}
		setIsSubmitting(true);
		try {
			const popup = await integrationService.connectShopifyIntegration(
				data.shop,
				String(currentUser?.accountId ?? ''),
				selectedScopes
			);
			if (!popup) {
				setIsSubmitting(false);
				return;
			}

			// The OAuth flow completes in the popup tab, not this one, so poll
			// integrations until *this store* shows connected (or the popup is closed
			// without completing). Matching on the shop handle, not just the provider
			// name: with a store already connected, "any Shopify row" is true at once.
			const handle = shopifyHandle(data.shop);
			pollTimerRef.current = window.setInterval(async () => {
				try {
					const result = await integrationsQuery.refetch();
					const updated = result.data ?? [];
					const store = updated.find(
						(i) => i.name?.toLowerCase() === 'shopify' && i.isConnected && shopifyHandle(i.authMetaData?.shop) === handle
					);
					if (store) form.reset({ shop: '' });
					if (store || popup.closed) {
						setIsSubmitting(false);
						if (popup && !popup.closed) popup.close();
						if (pollTimerRef.current) {
							window.clearInterval(pollTimerRef.current);
							pollTimerRef.current = null;
						}
					}
				} catch {
					// continue polling on transient errors
				}
			}, 2000);
		} catch (e) {
			console.error('Shopify connect failed', e);
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async (id: number | string) => {
		if (!(await confirmDisconnect('this Shopify store'))) return;
		disconnectMutation.mutate(String(id));
	};

	const handleMakePrimary = (id: number | string) => {
		setPrimaryMutation.mutate(String(id), {
			onSuccess: () => toast.success('Primary store updated.'),
			onError: () => toast.error("Couldn't update the primary store. Try again.")
		});
	};

	const busy = disconnectMutation.isPending || setPrimaryMutation.isPending;
	const storeList = stores.length > 0 && (
		<div className='flex flex-col gap-2'>
			<div className='flex flex-col gap-0.5'>
				<h3 className='text-sm font-semibold'>Connected stores</h3>
				<p className='text-xs text-muted-foreground'>
					The primary store is used whenever a conversation doesn't say which store it's about.
				</p>
			</div>
			<ul className='flex flex-col divide-y rounded-lg border'>
				{stores.map((store, index) => {
					const isPrimary = index === 0;
					const handle = shopifyHandle(store.authMetaData?.shop);
					return (
						<li key={String(store.id)} className='flex items-center justify-between gap-3 px-3 py-2'>
							<span className='truncate text-sm font-medium'>{handle || 'Unnamed store'}</span>
							<div className='flex shrink-0 items-center gap-2'>
								{isPrimary && (
									<span className='rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-600'>Primary</span>
								)}
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant='ghost' size='icon-sm' disabled={busy} aria-label={`Actions for ${handle}`}>
											<MoreHorizontal className='size-4' />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align='end'>
										<DropdownMenuItem onClick={() => handleMakePrimary(store.id)} disabled={isPrimary}>
											Make primary
										</DropdownMenuItem>
										<DropdownMenuItem variant='destructive' onClick={() => handleDisconnect(store.id)}>
											Disconnect
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</li>
					);
				})}
			</ul>
			<Separator className='mt-2' />
		</div>
	);

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					{storeList}
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>{stores.length > 0 ? 'Add another store' : `Link a ${integration.name} Account`}</h3>
						<p className='text-sm text-muted-foreground'>
							{stores.length > 0
								? 'Run more than one store? Connect it here; each store keeps its own permissions.'
								: 'Enter your Shopify store name to activate the integration.'}
						</p>
					</div>

					<FormField
						control={form.control}
						name='shop'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Shop Name</FormLabel>
								<FormControl>
									<Input placeholder='your-store' {...field} />
								</FormControl>
								<p className='text-xs text-muted-foreground'>e.g. your-store (from your-store.myshopify.com)</p>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className='flex flex-col gap-3'>
						<div className='flex flex-wrap items-center justify-between gap-2'>
							<Label className='text-sm font-medium'>
								Shopify permissions
								{scopeCatalog.length > 0 && (
									<span className='ml-2 font-normal text-muted-foreground'>
										{selectedScopes.length} of {scopeCatalog.length} selected
									</span>
								)}
							</Label>
							<div className={scopeCatalog.length === 0 ? 'hidden' : 'flex gap-2'}>
								{scopePresets.map((preset) => (
									<Button
										key={preset.label}
										type='button'
										variant='outline'
										size='sm'
										onClick={() => setSelectedScopes(preset.apply())}
									>
										{preset.label}
									</Button>
								))}
							</div>
						</div>

						{Object.keys(scopeGroups).length === 0 ? (
							<p className='text-xs text-muted-foreground'>
								{scopesFailed
									? "Couldn't load the permission list. Connecting will request our standard set — you'll review it on Shopify."
									: 'Loading permissions…'}
							</p>
						) : (
							<div className='flex max-h-64 flex-col gap-3 overflow-y-auto rounded-lg border p-3'>
								{Object.entries(scopeGroups).map(([category, scopes]) => (
									<div key={category} className='flex flex-col gap-2'>
										<Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
											{category}
										</Label>
										{scopes.map((scope) => (
											<label key={scope.value} className='flex items-start gap-2 cursor-pointer'>
												<input
													type='checkbox'
													checked={selectedScopes.includes(scope.value)}
													onChange={() => toggleScope(scope.value)}
													disabled={scope.required}
													className='mt-0.5'
												/>
												<span className='flex flex-col gap-0.5'>
													<span className='text-sm font-medium'>
														{scope.label}
														{scope.required && (
															<span className='ml-1.5 text-[10px] font-normal uppercase tracking-wide text-muted-foreground'>
																Required
															</span>
														)}
													</span>
													<span className='text-xs text-muted-foreground'>{scope.description}</span>
												</span>
											</label>
										))}
									</div>
								))}
							</div>
						)}
					</div>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const smartrrFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long')
});

type SmartrrFormData = z.infer<typeof smartrrFormSchema>;

const SmartrrContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<SmartrrFormData>({
		resolver: zodResolver(smartrrFormSchema),
		defaultValues: { apiKey: '' }
	});

	const handleConnect = async (data: SmartrrFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectSmartrrIntegration(data.apiKey);
			await refetch();
		} catch (e) {
			console.error('Smartrr connect failed', e);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Smartrr API key to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your Smartrr API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const skioFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long'),
	baseUrl: z.union([z.literal(''), z.string().url('Enter a valid URL')]).optional()
});

type SkioFormData = z.infer<typeof skioFormSchema>;

const SkioContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<SkioFormData>({
		resolver: zodResolver(skioFormSchema),
		defaultValues: { apiKey: '', baseUrl: '' }
	});

	const handleConnect = async (data: SkioFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectSkioIntegration(data.apiKey, data.baseUrl?.trim() || undefined);
			await refetch();
			toast.success('Skio connected');
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect Skio. Please check your API key and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Skio API key to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your Skio API key' {...field} />
								</FormControl>
								<p className='text-xs text-muted-foreground'>Found in your Skio dashboard under API &amp; Integrations → API.</p>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='baseUrl'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Base URL (optional)</FormLabel>
								<FormControl>
									<Input placeholder='https://graphql.skio.com/v1/graphql' {...field} />
								</FormControl>
								<p className='text-xs text-muted-foreground'>Leave blank unless you route Skio through a proxy.</p>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const smileFormSchema = z.object({
	apiKey: z.string().trim().min(1, 'API key is required').max(512, 'API key is too long')
});

/** The access scopes Kim's Smile tools call with; a key missing any of them connects but fails those lookups. */
const SMILE_REQUIRED_SCOPES = [
	'customer:read',
	'points_transaction:read',
	'points_transaction:write',
	'points_settings:read',
	'reward_fulfillment:read',
	'points_product:read',
	'points_purchase:write',
	'vip_tier:read',
	'earning_rule:read',
	'referral_settings:read'
];

type SmileFormData = z.infer<typeof smileFormSchema>;

const SmileContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<SmileFormData>({
		resolver: zodResolver(smileFormSchema),
		defaultValues: { apiKey: '' }
	});

	const handleConnect = async (data: SmileFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectSmileIntegration(data.apiKey);
			await refetch();
			toast.success('Smile.io connected');
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect Smile.io. Please check your API key and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Smile.io API key to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='api_…' {...field} />
								</FormControl>
								<p className='text-xs text-muted-foreground'>
									Create one in Smile Admin under Settings &rarr; Developer tools &rarr; Manage API keys (Plus or Enterprise plan). Tick
									these scopes when you create it: {SMILE_REQUIRED_SCOPES.join(', ')}.
								</p>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const infoplusFormSchema = z.object({
	subdomain: z.string().min(1, 'Subdomain is required').max(253, 'Subdomain is too long'),
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long')
});

type InfoPlusFormData = z.infer<typeof infoplusFormSchema>;

const InfoPlusContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<InfoPlusFormData>({
		resolver: zodResolver(infoplusFormSchema),
		defaultValues: { subdomain: '', apiKey: '' }
	});

	const handleConnect = async (data: InfoPlusFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectInfoplusIntegration(data.subdomain, data.apiKey);
			await refetch();
		} catch (e) {
			console.error('InfoPlus connect failed', e);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{integration.connectedName && (
					<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
						<Label className='text-xs text-muted-foreground'>Connected Account</Label>
						<span className='text-sm font-medium'>{integration.connectedName}</span>
					</div>
				)}

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link an {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>
							Enter your InfoPlus subdomain and API key to activate the integration.
						</p>
					</div>

					<FormField
						control={form.control}
						name='subdomain'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Subdomain</FormLabel>
								<FormControl>
									<Input placeholder='yourcompany (from yourcompany.infopluswms.com)' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your InfoPlus API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const zohoFormSchema = z.object({
	supportEmail: z.string().email('Enter a valid support email').max(254, 'Email is too long'),
	portalName: z.string().min(1, 'Portal name is required').max(100, 'Portal name is too long')
});

type ZohoFormData = z.infer<typeof zohoFormSchema>;

const ZohoContent = ({ integration }: IntegrationContentProps) => {
	const currentUser = useAtomValue(currentUserAtom);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [connecting, setConnecting] = useState(false);
	const [successToken, setSuccessToken] = useState<string | null>(null);
	const [showToken, setShowToken] = useState(false);
	// Use integrations query so we can trigger a refetch while polling
	const integrationsQuery = useIntegrations();
	const disconnectMutation = useDisconnectIntegration();

	const pollTimerRef = useRef<number | null>(null);
	const messageHandlerRef = useRef<((e: MessageEvent) => void) | null>(null);

	useEffect(() => {
		return () => {
			if (pollTimerRef.current) {
				window.clearInterval(pollTimerRef.current);
				pollTimerRef.current = null;
			}
			if (messageHandlerRef.current) {
				window.removeEventListener('message', messageHandlerRef.current);
				messageHandlerRef.current = null;
			}
		};
	}, []);

	const form = useForm<ZohoFormData>({
		resolver: zodResolver(zohoFormSchema),
		defaultValues: {
			supportEmail: '',
			portalName: ''
		}
	});

	const handleConnect = (data: ZohoFormData) => {
		setIsSubmitting(true);
		const backendUrl = import.meta.env.VITE_MACHINE_BASE_URL || 'https://api.dev.kim.cc/api';
		const accountId = currentUser?.accountId || '1';
		const fromEmail = encodeURIComponent(data.supportEmail);
		const portalName = encodeURIComponent(data.portalName.trim());

		// Open the OAuth flow in a popup and poll integrations until API key appears
		const popup = window.open(
			`${backendUrl}/zoho/oauth/install?kim_account_id=${encodeURIComponent(accountId)}&from_email=${fromEmail}&portal_name=${portalName}`,
			'zoho_oauth',
			'width=900,height=700'
		);

		setConnecting(true);
		setIsSubmitting(false);

		// Message handler to receive token from popup via postMessage
		// Backend currently posts a payload JSON that contains `zoho_app_token` and other fields.
		// Accept messages that include `zoho_app_token` (no strict origin check because backend may post '*').
		const onMessage = (e: MessageEvent) => {
			try {
				const data = (e && (e.data as Record<string, unknown>)) || {};
				// Accept payloads that include the token (backend sends zoho_app_token)
				if (data && typeof data.zoho_app_token === 'string' && data.zoho_app_token.length > 0) {
					const token = String(data.zoho_app_token);
					setSuccessToken(token);
					setConnecting(false);
					if (popup && !popup.closed) popup.close();
					if (pollTimerRef.current) {
						window.clearInterval(pollTimerRef.current);
						pollTimerRef.current = null;
					}
					if (messageHandlerRef.current) {
						window.removeEventListener('message', messageHandlerRef.current);
						messageHandlerRef.current = null;
					}
				}
			} catch {
				// ignore
			}
		};

		messageHandlerRef.current = onMessage;
		window.addEventListener('message', onMessage);

		const checkInterval = 2000;
		pollTimerRef.current = window.setInterval(async () => {
			try {
				// Refetch integrations to get updated authMetaData
				const result = integrationsQuery?.refetch ? await integrationsQuery.refetch() : undefined;
				const apiIntegrations = (result?.data || []) as IntegrationAccount[];
				const zoho = apiIntegrations.find((i) => i.name?.toLowerCase() === 'zoho');

				// Look for common key names that may contain the returned API token
				const meta = zoho?.authMetaData || {};
				const possibleKey = meta?.zoho_app_token || meta?.api_key || meta?.apiKey || meta?.key || meta?.token || null;

				if (possibleKey) {
					setSuccessToken(String(possibleKey));
					setConnecting(false);
					if (popup && !popup.closed) popup.close();
					if (pollTimerRef.current) {
						window.clearInterval(pollTimerRef.current);
						pollTimerRef.current = null;
					}
					if (messageHandlerRef.current) {
						window.removeEventListener('message', messageHandlerRef.current);
						messageHandlerRef.current = null;
					}
				} else if (popup && popup.closed) {
					// Popup closed without token — stop polling
					setConnecting(false);
					if (pollTimerRef.current) {
						window.clearInterval(pollTimerRef.current);
						pollTimerRef.current = null;
					}
					if (messageHandlerRef.current) {
						window.removeEventListener('message', messageHandlerRef.current);
						messageHandlerRef.current = null;
					}
				}
			} catch {
				// continue polling on errors
			}
		}, checkInterval);
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id, {
			onSuccess: () => {
				setSuccessToken(null);
				setShowToken(false);
			}
		});
	};

	// Extract API key from authMetaData (backend stores as zoho_app_token in custom_attributes)
	const getApiKeyFromMeta = () => {
		const meta = integration.authMetaData as Record<string, unknown> | undefined;
		if (!meta) return null;
		return (meta.zoho_app_token ?? meta.api_key ?? meta.apiKey ?? meta.key ?? meta.token) as string | undefined;
	};

	const existingApiKey = getApiKeyFromMeta();
	const displayToken = successToken || existingApiKey;

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{integration.connectedName && (
					<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
						<Label className='text-xs text-muted-foreground'>Connected Account</Label>
						<span className='text-sm font-medium'>{integration.connectedName}</span>
					</div>
				)}

				{/* Show API key if available */}
				{displayToken && (
					<div className='flex flex-col gap-2 p-4 bg-muted rounded-lg'>
						<div className='flex items-center gap-2 mb-1'>
							<div className='h-5 w-5 rounded-full bg-green-500 flex items-center justify-center text-white text-xs'>✓</div>
							<Label className='text-xs text-muted-foreground'>Your API Key</Label>
						</div>
						<div className='flex items-center gap-2'>
							<code className='flex-1 bg-neutral-100 border border-neutral-200 text-neutral-800 p-2 rounded-md overflow-auto text-xs font-mono'>
								{showToken ? displayToken : maskApiKey(displayToken)}
							</code>
							<Button
								type='button'
								size='icon-sm'
								variant='ghost'
								onClick={() => setShowToken(!showToken)}
								title={showToken ? 'Hide API key' : 'Show API key'}
							>
								{showToken ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
							</Button>
							<Button
								type='button'
								size='sm'
								variant='outline'
								onClick={async () => {
									try {
										await navigator.clipboard.writeText(displayToken);
										toast.success('Copied to clipboard');
									} catch (e) {
										console.warn('Clipboard write failed', e);
									}
								}}
							>
								Copy
							</Button>
						</div>
						<p className='text-xs text-muted-foreground'>Use this API key in the Zoho Desk extension.</p>
					</div>
				)}
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your support email to activate the integration.</p>
					</div>

					<div className='flex flex-col gap-4'>
						<FormField
							control={form.control}
							name='supportEmail'
							render={({ field }) => (
								<FormItem>
									<FormLabel>Support Email</FormLabel>
									<FormControl>
										<Input placeholder='support@example.com' {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name='portalName'
							render={({ field }) => (
								<FormItem>
									<FormLabel>Portal Name</FormLabel>
									<FormControl>
										<Input placeholder='e.g. rforrabbit' {...field} />
									</FormControl>
									<p className='text-xs text-muted-foreground'>
										Found in your Zoho Desk URL: desk.zoho.com/agent/<strong>portal-name</strong>/…
									</p>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					{/* Show API key after successful OAuth or if already in authMetaData */}
					{displayToken && (
						<div className='flex flex-col gap-2 p-4 bg-muted rounded-lg'>
							<div className='flex items-center gap-3'>
								<div className='h-8 w-8 rounded-full bg-green-500 flex items-center justify-center text-white'>✓</div>
								<div className='flex-1'>
									<Label className='text-sm font-medium'>Zoho Integration Connected</Label>
									<div className='text-xs text-muted-foreground'>Successfully connected — copy the API key below.</div>
								</div>
							</div>

							<div className='flex items-center gap-2 mt-3'>
								<code className='flex-1 bg-neutral-100 border border-neutral-200 text-neutral-800 p-2 rounded-md overflow-auto text-xs font-mono'>
									{showToken ? displayToken : maskApiKey(displayToken)}
								</code>
								<Button
									type='button'
									size='icon-sm'
									variant='ghost'
									onClick={() => setShowToken(!showToken)}
									title={showToken ? 'Hide API key' : 'Show API key'}
								>
									{showToken ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
								</Button>
								<Button
									type='button'
									size='sm'
									variant='outline'
									onClick={async () => {
										try {
											await navigator.clipboard.writeText(displayToken);
											toast.success('Copied to clipboard');
										} catch (e) {
											console.warn('Clipboard write failed', e);
										}
									}}
								>
									Copy
								</Button>
							</div>
							<p className='text-xs text-muted-foreground'>Save this API key — you'll need it for the Zoho Desk extension.</p>
						</div>
					)}

					{/* Show lightweight connecting indicator if popup open */}
					{connecting && !displayToken && (
						<div className='flex items-center gap-2 p-3'>
							<div className='h-2 w-2 rounded-full bg-blue-500 animate-pulse' />
							<div className='text-xs text-muted-foreground'>Waiting for Zoho to complete authorization…</div>
						</div>
					)}
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting || !!displayToken}>
						<Radio className='size-4' />
						{displayToken ? 'Connected' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const zendeskFormSchema = z.object({
	subdomain: z
		.string()
		.min(1, 'Subdomain is required')
		.max(253, 'Subdomain is too long')
		.regex(/^[a-z0-9-]+$/i, 'Enter the bare subdomain only (e.g. yourcompany, not yourcompany.zendesk.com)')
});

type ZendeskFormData = z.infer<typeof zendeskFormSchema>;

const ZendeskContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const integrationsQuery = useIntegrations();
	const pollTimerRef = useRef<number | null>(null);

	useEffect(() => {
		return () => {
			if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
		};
	}, []);

	const form = useForm<ZendeskFormData>({
		resolver: zodResolver(zendeskFormSchema),
		defaultValues: { subdomain: '' }
	});

	const handleConnect = async (data: ZendeskFormData) => {
		setIsSubmitting(true);
		try {
			const popup = await integrationService.connectZendeskIntegration(data.subdomain);
			if (!popup) {
				setIsSubmitting(false);
				return;
			}

			// The consent flow completes in the popup tab, not this one, so poll
			// integrations until this one shows connected (or the popup is closed
			// without completing) instead of refetching once immediately.
			pollTimerRef.current = window.setInterval(async () => {
				try {
					const result = await integrationsQuery.refetch();
					const updated = result.data ?? [];
					const zendesk = updated.find((i) => i.name?.toLowerCase() === 'zendesk' && i.isConnected);
					if (zendesk || popup.closed) {
						setIsSubmitting(false);
						if (popup && !popup.closed) popup.close();
						if (pollTimerRef.current) {
							window.clearInterval(pollTimerRef.current);
							pollTimerRef.current = null;
						}
					}
				} catch {
					// continue polling on transient errors
				}
			}, 2000);
		} catch (err) {
			const errorResponse = (
				err as {
					response?: {
						data?: {
							error?: string;
							message?: string;
						};
					};
				}
			)?.response?.data;

			const errorMessage = errorResponse?.error ?? errorResponse?.message ?? 'Failed to connect Zendesk';
			toast.error(errorMessage);
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{integration.connectedName && (
					<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
						<Label className='text-xs text-muted-foreground'>Connected Account</Label>
						<span className='text-sm font-medium'>{integration.connectedName}</span>
					</div>
				)}
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>
							Enter your Zendesk subdomain. You&apos;ll then authorize Kim in Zendesk — we never store your Zendesk
							credentials.
						</p>
					</div>

					<FormField
						control={form.control}
						name='subdomain'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Subdomain</FormLabel>
								<FormControl>
									<Input placeholder='yourcompany (from yourcompany.zendesk.com)' {...field} />
								</FormControl>
								<p className='text-xs text-muted-foreground'>
									Sign in as a Zendesk <span className='font-medium'>admin</span> when authorizing — Kim needs admin
									permissions to set up the webhook and view, so the connection will fail otherwise.
								</p>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const loopReturnsFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long')
});

type LoopReturnsFormData = z.infer<typeof loopReturnsFormSchema>;

const LoopReturnsContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<LoopReturnsFormData>({
		resolver: zodResolver(loopReturnsFormSchema),
		defaultValues: { apiKey: '' }
	});

	const handleConnect = async (data: LoopReturnsFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectLoopReturnsIntegration(data.apiKey);
			await refetch();
		} catch (e) {
			console.error('Loop Returns connect failed', e);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Loop Returns API key to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your Loop Returns API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const GoogleContent = ({ integration }: IntegrationContentProps) => {
	const currentUser = useAtomValue(currentUserAtom);
	const [connecting, setConnecting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const integrationsQuery = useIntegrations();
	const pollTimerRef = useRef<number | null>(null);

	useEffect(() => {
		return () => {
			if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
		};
	}, []);

	const handleConnect = () => {
		const backendUrl = import.meta.env.VITE_MACHINE_BASE_URL || 'https://api.dev.kim.cc/api';
		const accountId = currentUser?.accountId || '';

		const popup = window.open(
			`${backendUrl}/integrations/google/oauth/install?state=${encodeURIComponent(accountId)}`,
			'google_oauth',
			'width=600,height=700',
		);

		setConnecting(true);

		pollTimerRef.current = window.setInterval(async () => {
			try {
				const result = await integrationsQuery.refetch();
				const updated = result.data ?? [];
				const google = updated.find((i) => i.name?.toLowerCase() === 'google' && i.isConnected);
				if (google) {
					setConnecting(false);
					if (popup && !popup.closed) popup.close();
					if (pollTimerRef.current) {
						window.clearInterval(pollTimerRef.current);
						pollTimerRef.current = null;
					}
				} else if (popup?.closed) {
					setConnecting(false);
					if (pollTimerRef.current) {
						window.clearInterval(pollTimerRef.current);
						pollTimerRef.current = null;
					}
				}
			} catch {
				// continue polling
			}
		}, 2000);
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected Google Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Docs and Sheets from this account can be used as sources.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{integration.connectedName && (
					<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
						<Label className='text-xs text-muted-foreground'>Connected Account</Label>
						<span className='text-sm font-medium'>{integration.connectedName}</span>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className='flex flex-col h-full'>
			<div className='flex flex-col gap-4 px-5 flex-1'>
				<div className='flex flex-col gap-1'>
					<h3 className='text-sm font-semibold'>Link a Google Account</h3>
					<p className='text-sm text-muted-foreground'>
						Connect your Google account to use Docs and Sheets as knowledge sources in Kim.
					</p>
				</div>

				{connecting && (
					<div className='flex items-center gap-2 p-3'>
						<div className='h-2 w-2 rounded-full bg-blue-500 animate-pulse' />
						<div className='text-xs text-muted-foreground'>Waiting for Google to complete authorization…</div>
					</div>
				)}
			</div>

			<DialogFooter className='px-5 pb-5 pt-4'>
				<Button className='w-full' size='lg' onClick={handleConnect} disabled={connecting}>
					<Radio className='size-4' />
					{connecting ? 'Connecting…' : 'Connect with Google'}
				</Button>
			</DialogFooter>
		</div>
	);
};

const clickpostFormSchema = z.object({
	key: z.string().min(1, 'Key is required').max(256, 'Key is too long'),
	username: z.string().min(1, 'Username is required').max(256, 'Username is too long'),
	password: z.string().min(1, 'Password is required').max(256, 'Password is too long')
});

type ClickPostFormData = z.infer<typeof clickpostFormSchema>;

const ClickPostContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<ClickPostFormData>({
		resolver: zodResolver(clickpostFormSchema),
		defaultValues: { key: '', username: '', password: '' }
	});

	const handleConnect = async (data: ClickPostFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectClickpostIntegration(data.key, data.username, data.password);
			await refetch();
		} catch (err) {
			const errorResponse = (
				err as {
					response?: {
						data?: {
							error?: string;
							message?: string;
						};
					};
				}
			)?.response?.data;

			const errorMessage =
				errorResponse?.error ??
				errorResponse?.message ??
				'Failed to connect ClickPost';

			toast.error(errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{integration.connectedName && (
					<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
						<Label className='text-xs text-muted-foreground'>Connected Account</Label>
						<span className='text-sm font-medium'>{integration.connectedName}</span>
					</div>
				)}

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>
							Enter your {integration.name} key, username and password to activate the integration.
						</p>
					</div>

					<FormField
						control={form.control}
						name='key'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Key</FormLabel>
								<FormControl>
									<Input placeholder='Enter your ClickPost API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='username'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Username</FormLabel>
								<FormControl>
									<Input placeholder='Enter your ClickPost username' autoComplete='username' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='password'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Password</FormLabel>
								<FormControl>
									<Input type='password' placeholder='Enter your ClickPost password' autoComplete='current-password' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const shipstationFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long')
});

type ShipstationFormData = z.infer<typeof shipstationFormSchema>;

const ShipstationContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<ShipstationFormData>({
		resolver: zodResolver(shipstationFormSchema),
		defaultValues: { apiKey: '' }
	});

	const handleConnect = async (data: ShipstationFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectShipstationIntegration(data.apiKey);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
			const errorMessage = errorResponse?.error ?? errorResponse?.message ?? 'Failed to connect ShipStation';
			toast.error(errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your ShipStation API key to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your ShipStation API key' {...field} />
								</FormControl>
								<p className='text-xs text-muted-foreground'>Found in ShipStation under Account Settings → API Settings.</p>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const yotpoFormSchema = z.object({
	guid: z.string().max(256, 'GUID is too long').optional(),
	apiKey: z.string().max(512, 'API key is too long').optional(),
	merchantId: z.string().max(256, 'Merchant ID is too long').optional(),
	appKey: z.string().min(1, 'App key is required').max(512, 'App key is too long'),
	secret: z.string().min(1, 'Secret is required').max(512, 'Secret is too long')
});

type YotpoFormData = z.infer<typeof yotpoFormSchema>;

const YotpoContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<YotpoFormData>({
		resolver: zodResolver(yotpoFormSchema),
		defaultValues: { guid: '', apiKey: '', merchantId: '', appKey: '', secret: '' }
	});

	const handleConnect = async (data: YotpoFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectYotpoIntegration(data.guid || undefined, data.apiKey || undefined, data.appKey, data.secret, data.merchantId || undefined);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
			const errorMessage = errorResponse?.error ?? errorResponse?.message ?? 'Failed to connect Yotpo';
			toast.error(errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Yotpo credentials to activate loyalty and reviews.</p>
					</div>

					<p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Loyalty <span className='normal-case font-normal'>(optional)</span></p>

					<FormField
						control={form.control}
						name='guid'
						render={({ field }) => (
							<FormItem>
								<FormLabel>GUID</FormLabel>
								<FormControl>
									<Input placeholder='Enter your Yotpo GUID' {...field} />
								</FormControl>
								<p className='text-xs text-muted-foreground'>Found in Yotpo Loyalty dashboard under Settings → API.</p>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your Yotpo Loyalty API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='merchantId'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Merchant ID <span className='text-muted-foreground font-normal'>(optional)</span></FormLabel>
								<FormControl>
									<Input placeholder='Enter your Yotpo Merchant ID' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Reviews</p>

					<FormField
						control={form.control}
						name='appKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>App Key</FormLabel>
								<FormControl>
									<Input placeholder='Enter your Yotpo App Key' {...field} />
								</FormControl>
								<p className='text-xs text-muted-foreground'>Found under Settings → Store Settings → API.</p>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='secret'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Secret</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your Yotpo secret' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const judgemeFormSchema = z.object({
	domain: z.string().min(1, 'Domain is required').max(253, 'Domain is too long'),
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long')
});

type JudgemeFormData = z.infer<typeof judgemeFormSchema>;

const aftershipFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long')
});

type AfterShipFormData = z.infer<typeof aftershipFormSchema>;

const AfterShipContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<AfterShipFormData>({
		resolver: zodResolver(aftershipFormSchema),
		defaultValues: { apiKey: '' }
	});

	const handleConnect = async (data: AfterShipFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectAftershipIntegration(data.apiKey);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect AfterShip. Please check your API key and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link an AfterShip Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your AfterShip API key to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your AfterShip API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect AfterShip'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const wondermentFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long')
});

type WondermentFormData = z.infer<typeof wondermentFormSchema>;

const WondermentContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<WondermentFormData>({
		resolver: zodResolver(wondermentFormSchema),
		defaultValues: { apiKey: '' }
	});

	const handleConnect = async (data: WondermentFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectWondermentIntegration(data.apiKey);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect Wonderment. Please check your API key and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a Wonderment Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Wonderment API key to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your Wonderment API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect Wonderment'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

// Checkout Champ has no API keys: it authenticates as a dedicated "Api User" (Admin → Users → Add User).
// Its password cannot contain "&" — Checkout Champ rejects it there, so catch it before the round trip.
// Checkout Champ only answers API calls from IPs the API user allows; Kim's calls leave through these.
const CHECKOUTCHAMP_ALLOWED_IPS = [
	{ label: 'Primary IP', ip: '54.175.244.34' },
	{ label: 'Secondary IP', ip: '16.170.24.90' }
];
const checkoutChampFormSchema = z.object({
	loginId: z.string().trim().min(1, 'API username is required').max(32, 'Checkout Champ usernames are at most 32 characters'),
	password: z.string().min(1, 'Password is required').max(32, 'Checkout Champ passwords are at most 32 characters').refine((v) => !v.includes('&'), 'Checkout Champ passwords cannot contain "&"')
});

type CheckoutChampFormData = z.infer<typeof checkoutChampFormSchema>;

const CheckoutChampContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<CheckoutChampFormData>({
		resolver: zodResolver(checkoutChampFormSchema),
		defaultValues: { loginId: '', password: '' }
	});

	const handleConnect = async (data: CheckoutChampFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectCheckoutChampIntegration(data.loginId, data.password);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect Checkout Champ. Please check the API user credentials and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		const loginId = String(integration.authMetaData?.login_id ?? '');
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>API User</Label>
					<span className='text-sm font-medium'>{loginId || 'Connected'}</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a Checkout Champ Account</h3>
						<ol className='list-decimal pl-4 text-sm text-muted-foreground space-y-0.5'>
							<li>In Checkout Champ, open Admin → Users and click + to add a user.</li>
							<li>Set Access Level to "Api User". Don't use your admin login.</li>
							<li>Under IP Whitelist, add both IPs shown below.</li>
							<li>Under Allowed API Endpoints, choose Select All.</li>
							<li>Set a password of up to 32 characters with no "&", then enter the username and password here.</li>
						</ol>
						<p className='text-xs text-muted-foreground'>Kim verifies the credentials before saving them.</p>
					</div>

					<div className='flex flex-col gap-3 p-3 bg-muted rounded-lg'>
						{CHECKOUTCHAMP_ALLOWED_IPS.map(({ label, ip }) => (
							<CopyField key={ip} label={label} value={ip} />
						))}
					</div>

					<FormField
						control={form.control}
						name='loginId'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Username</FormLabel>
								<FormControl>
									<Input autoComplete='off' placeholder='Enter the API user username' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='password'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Password</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter the API user password' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect Checkout Champ'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const JudgemeContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<JudgemeFormData>({
		resolver: zodResolver(judgemeFormSchema),
		defaultValues: { domain: '', apiKey: '' }
	});

	const handleConnect = async (data: JudgemeFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectJudgemeIntegration(data.domain, data.apiKey);
			await refetch();
		} catch (err) {
			const errorResponse = (
				err as {
					response?: {
						data?: {
							error?: string;
							message?: string;
						};
					};
				}
			)?.response?.data;

			const errorMessage = errorResponse?.error ?? errorResponse?.message ?? 'Failed to connect Judge.me';
			toast.error(errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Agent</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{integration.connectedName && (
					<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
						<Label className='text-xs text-muted-foreground'>Connected Store</Label>
						<span className='text-sm font-medium'>{integration.connectedName}</span>
					</div>
				)}
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a {integration.name} Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Judge.me domain and API key to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='domain'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Domain</FormLabel>
								<FormControl>
									<Input placeholder='yourstore.myshopify.com' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your Judge.me API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

type ShipbobChannel = { id: number; name: string; application_name: string };

const ShipBobContent = ({ integration }: IntegrationContentProps) => {
	const [pat, setPat] = useState('');
	const [channels, setChannels] = useState<ShipbobChannel[]>([]);
	const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
	const [isFetching, setIsFetching] = useState(false);
	const [isConnecting, setIsConnecting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const handleFetchChannels = async () => {
		if (!pat.trim()) return;
		setIsFetching(true);
		try {
			const list = await integrationService.fetchShipbobChannels(pat.trim());
			setChannels(list);
			setSelectedChannelId(list[0]?.id ?? null);
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to fetch ShipBob channels. Check your PAT.');
		} finally {
			setIsFetching(false);
		}
	};

	const handleConnect = async () => {
		if (!selectedChannelId) return;
		setIsConnecting(true);
		try {
			await integrationService.connectShipbobIntegration(pat.trim(), selectedChannelId);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect ShipBob.');
		} finally {
			setIsConnecting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<div className='flex flex-col h-full'>
			<div className='flex flex-col gap-4 px-5 flex-1'>
				<div className='flex flex-col gap-1'>
					<h3 className='text-sm font-semibold'>Link a ShipBob Account</h3>
					<p className='text-sm text-muted-foreground'>Enter your ShipBob Personal Access Token, then select a channel to connect.</p>
				</div>

				<div className='flex flex-col gap-2'>
					<Label>Personal Access Token</Label>
					<div className='flex gap-2'>
						<Input
							type='password'
							placeholder='Enter your ShipBob PAT'
							value={pat}
							onChange={(e) => { setPat(e.target.value); setChannels([]); setSelectedChannelId(null); }}
						/>
						<Button type='button' variant='outline' onClick={handleFetchChannels} disabled={isFetching || !pat.trim()}>
							{isFetching ? 'Loading…' : 'Load'}
						</Button>
					</div>
				</div>

				{channels.length > 0 && (
					<div className='flex flex-col gap-2'>
						<Label>Select Channel</Label>
						<div className='flex flex-col gap-2'>
							{channels.map((c) => (
								<label key={c.id} className='flex items-center gap-2 cursor-pointer'>
									<input
										type='radio'
										name='shipbob-channel'
										value={c.id}
										checked={selectedChannelId === c.id}
										onChange={() => setSelectedChannelId(c.id)}
									/>
									<span className='text-sm'>{c.name} <span className='text-muted-foreground text-xs'>({c.application_name})</span></span>
								</label>
							))}
						</div>
					</div>
				)}
			</div>

			<DialogFooter className='px-5 pb-5 pt-4'>
				<Button type='button' className='w-full' size={'lg'} onClick={handleConnect} disabled={isConnecting || !selectedChannelId}>
					<Radio className='size-4' />
					{isConnecting ? 'Connecting…' : 'Connect ShipBob'}
				</Button>
			</DialogFooter>
		</div>
	);
};

const slackFormSchema = z.object({
	botToken: z.string().min(1, 'Bot token is required').max(512, 'Bot token is too long').refine(
		(v) => v.startsWith('xoxb-'),
		{ message: 'Must be a Slack bot token (starts with xoxb-)' }
	)
});

type SlackFormData = z.infer<typeof slackFormSchema>;

const SlackContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<SlackFormData>({
		resolver: zodResolver(slackFormSchema),
		defaultValues: { botToken: '' }
	});

	const handleConnect = async (data: SlackFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectSlackIntegration(data.botToken);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
			const errorMessage = errorResponse?.error ?? errorResponse?.message ?? 'Failed to connect Slack';
			toast.error(errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Workspace</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a Slack Workspace</h3>
						<p className='text-sm text-muted-foreground'>Enter a Slack bot token to give Kim access to your workspace channels and messages.</p>
					</div>

					<FormField
						control={form.control}
						name='botToken'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Bot Token</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='xoxb-...' {...field} />
								</FormControl>
								<p className='text-xs text-muted-foreground'>
									Create a Slack app, add the required OAuth scopes, install it to your workspace, and copy the Bot User OAuth Token from the OAuth &amp; Permissions page.
								</p>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const klaviyoFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long')
});

type KlaviyoFormData = z.infer<typeof klaviyoFormSchema>;

const KlaviyoContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<KlaviyoFormData>({
		resolver: zodResolver(klaviyoFormSchema),
		defaultValues: { apiKey: '' }
	});

	const handleConnect = async (data: KlaviyoFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectKlaviyoIntegration(data.apiKey);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect Klaviyo. Please check your API key and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a Klaviyo Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Klaviyo private API key to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Private API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='pk_...' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect Klaviyo'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const uspsFormSchema = z.object({
	clientId: z.string().min(1, 'Client ID is required').max(512, 'Client ID is too long'),
	clientSecret: z.string().min(1, 'Client secret is required').max(512, 'Client secret is too long')
});

type USPSFormData = z.infer<typeof uspsFormSchema>;

const USPSContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<USPSFormData>({
		resolver: zodResolver(uspsFormSchema),
		defaultValues: { clientId: '', clientSecret: '' }
	});

	const handleConnect = async (data: USPSFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectUspsIntegration(data.clientId, data.clientSecret);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect USPS. Please check your credentials and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Client ID</Label>
					<span className='text-sm font-medium'>{String(integration.authMetaData?.client_id ?? '')}</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a USPS Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your USPS Web Tools client ID and secret to activate tracking.</p>
					</div>

					<FormField
						control={form.control}
						name='clientId'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Client ID</FormLabel>
								<FormControl>
									<Input placeholder='Enter your USPS client ID' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='clientSecret'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Client Secret</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your USPS client secret' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect USPS'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const royalMailFormSchema = z.object({
	clientId: z.string().min(1, 'Client ID is required').max(512, 'Client ID is too long'),
	clientSecret: z.string().min(1, 'Client secret is required').max(512, 'Client secret is too long')
});

type RoyalMailFormData = z.infer<typeof royalMailFormSchema>;

const RoyalMailContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<RoyalMailFormData>({
		resolver: zodResolver(royalMailFormSchema),
		defaultValues: { clientId: '', clientSecret: '' }
	});

	const handleConnect = async (data: RoyalMailFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectRoyalMailIntegration(data.clientId, data.clientSecret);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect Royal Mail. Please check your credentials and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Client ID</Label>
					<span className='text-sm font-medium'>{String(integration.authMetaData?.client_id ?? '')}</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a Royal Mail Account</h3>
						<p className='text-sm text-muted-foreground'>
							Enter the client ID and secret of your Royal Mail developer-portal application subscribed to the Tracking API. Kim
							verifies them before saving.
						</p>
					</div>

					<FormField
						control={form.control}
						name='clientId'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Client ID</FormLabel>
								<FormControl>
									<Input placeholder='Enter your Royal Mail client ID' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='clientSecret'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Client Secret</FormLabel>
								<FormControl>
									<Input
										type='password'
										autoComplete='new-password'
										placeholder='Enter your Royal Mail client secret'
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect Royal Mail'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};


const reviewsioFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long'),
	storeId: z.string().min(1, 'Store ID is required').max(512, 'Store ID is too long')
});

type ReviewsioFormData = z.infer<typeof reviewsioFormSchema>;

const ReviewsioContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<ReviewsioFormData>({
		resolver: zodResolver(reviewsioFormSchema),
		defaultValues: { apiKey: '', storeId: '' }
	});

	const handleConnect = async (data: ReviewsioFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectReviewsioIntegration(data.apiKey, data.storeId);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect Reviews.io. Please check your credentials and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Store ID</Label>
					<span className='text-sm font-medium'>{String(integration.authMetaData?.store_id ?? '')}</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a Reviews.io Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Reviews.io API key and store ID to activate the integration.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your Reviews.io API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='storeId'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Store ID</FormLabel>
								<FormControl>
									<Input placeholder='Enter your Reviews.io store ID' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect Reviews.io'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const trustpilotFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long'),
	businessUnitId: z.string().max(512, 'Business unit ID is too long').optional(),
	apiSecret: z.string().max(512, 'API secret is too long').optional()
});

type TrustpilotFormData = z.infer<typeof trustpilotFormSchema>;

const TrustpilotContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<TrustpilotFormData>({
		resolver: zodResolver(trustpilotFormSchema),
		defaultValues: { apiKey: '', businessUnitId: '', apiSecret: '' }
	});

	const handleConnect = async (data: TrustpilotFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectTrustpilotIntegration(data.apiKey, data.businessUnitId, data.apiSecret);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect Trustpilot. Please check your credentials and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Business Unit ID</Label>
					<span className='text-sm font-medium'>{String(integration.authMetaData?.business_unit_id ?? '')}</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a Trustpilot Account</h3>
						<p className='text-sm text-muted-foreground'>Enter your Trustpilot API key to activate the integration. Add a business unit ID and API secret to enable replying to reviews.</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Enter your Trustpilot API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='businessUnitId'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Business Unit ID (optional)</FormLabel>
								<FormControl>
									<Input placeholder='Enter your Trustpilot business unit ID' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='apiSecret'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Secret (optional)</FormLabel>
								<FormControl>
									<Input type='password' autoComplete='new-password' placeholder='Required to reply to reviews' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect Trustpilot'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const kustomerFormSchema = z.object({
	subdomain: z
		.string()
		.transform(normalizeKustomerSubdomain)
		.pipe(
			z
				.string()
				.min(1, 'Subdomain is required')
				.regex(KUSTOMER_SUBDOMAIN_PATTERN, 'Enter a valid Kustomer subdomain, host, or URL')
		),
	apiKey: z.string().trim().min(1, 'API key is required').max(512)
});

type KustomerFormData = z.infer<typeof kustomerFormSchema>;

const KustomerContent = ({ integration, onOpenChange }: IntegrationContentProps & { onOpenChange?: (open: boolean) => void }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<KustomerFormData>({
		resolver: zodResolver(kustomerFormSchema),
		defaultValues: { subdomain: '', apiKey: '' }
	});

	const handleConnect = async (data: KustomerFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectKustomerIntegration(data.subdomain, data.apiKey);
			await refetch();
			onOpenChange?.(false);
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(
				errorResponse?.error ?? 'Failed to connect Kustomer. Please check your subdomain and API key and try again.'
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		const connectedOrgName = String(integration.authMetaData?.org_name ?? '').trim();

		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Connected domain</Label>
					<span className='text-sm font-medium'>{connectedOrgName ? `${connectedOrgName}.kustomerapp.com` : ''}</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a Kustomer Account</h3>
						<p className='text-sm text-muted-foreground'>
							Enter your Kustomer subdomain and API key. Kim will finish the required account setup automatically.
						</p>
					</div>

					<FormField
						control={form.control}
						name='subdomain'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Subdomain</FormLabel>
								<FormControl>
									<Input
										placeholder='yourcompany or yourcompany.kustomerapp.com'
										{...field}
										onBlur={() => {
											field.onChange(normalizeKustomerSubdomain(field.value));
											field.onBlur();
											void form.trigger('subdomain');
										}}
									/>
								</FormControl>
								<p className='text-xs text-muted-foreground'>
									You can paste a full Kustomer URL. It will be converted to the lowercase org slug shown here.
								</p>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' placeholder='Enter your Kustomer API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect Kustomer'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const warehanceFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long'),
	clientId: z.string().max(64, 'Client ID is too long').optional()
});

type WarehanceFormData = z.infer<typeof warehanceFormSchema>;

const WarehanceContent = ({ integration }: IntegrationContentProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const form = useForm<WarehanceFormData>({
		resolver: zodResolver(warehanceFormSchema),
		defaultValues: { apiKey: '', clientId: '' }
	});

	const handleConnect = async (data: WarehanceFormData) => {
		setIsSubmitting(true);
		try {
			await integrationService.connectWarehanceIntegration(data.apiKey, data.clientId);
			await refetch();
		} catch (err) {
			const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data;
			toast.error(errorResponse?.error ?? 'Failed to connect Warehance. Please check your credentials and try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		const clientId = String(integration.authMetaData?.client_id ?? '');
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Key Scope</Label>
					<span className='text-sm font-medium'>{clientId ? `Organization key · client ${clientId}` : 'Client-level key'}</span>
				</div>
			</div>
		);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleConnect)} className='flex flex-col h-full'>
				<div className='flex flex-col gap-4 px-5 flex-1'>
					<div className='flex flex-col gap-1'>
						<h3 className='text-sm font-semibold'>Link a Warehance Account</h3>
						<p className='text-sm text-muted-foreground'>
							Generate an API key in Warehance under Settings → API Keys, then paste it here. Kim verifies the key before saving it.
						</p>
					</div>

					<FormField
						control={form.control}
						name='apiKey'
						render={({ field }) => (
							<FormItem>
								<FormLabel>API Key</FormLabel>
								<FormControl>
									<Input type='password' placeholder='Enter your Warehance API key' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name='clientId'
						render={({ field }) => (
							<FormItem>
								<FormLabel>Client ID (optional)</FormLabel>
								<FormControl>
									<Input placeholder='Only for organization-level keys' {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className='px-5 pb-5 pt-4'>
					<Button type='submit' className='w-full' size={'lg'} disabled={isSubmitting}>
						<Radio className='size-4' />
						{isSubmitting ? 'Connecting…' : 'Connect Warehance'}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

const copyToClipboard = async (value: string) => {
	try {
		await navigator.clipboard.writeText(value);
		toast.success('Copied to clipboard');
	} catch (e) {
		console.warn('Clipboard write failed', e);
	}
};

// A labelled, copyable value row (Richpanel webhook setup, Checkout Champ allowed IPs). When `copyKey` is set
// (header rows), the label carries its own copy button for the raw header NAME — the user pastes
// both the key and the value into Richpanel's HTTP Target form, so both must be copyable.
const CopyField = ({
	label,
	value,
	secret = false,
	multiline = false,
	copyKey
}: {
	label: string;
	value: string;
	secret?: boolean;
	multiline?: boolean;
	copyKey?: string;
}) => {
	const [shown, setShown] = useState(false);
	const [copied, setCopied] = useState(false);
	const [keyCopied, setKeyCopied] = useState(false);

	const handleCopy = async () => {
		await copyToClipboard(value);
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1200);
	};

	const handleCopyKey = async () => {
		if (!copyKey) return;
		await copyToClipboard(copyKey);
		setKeyCopied(true);
		window.setTimeout(() => setKeyCopied(false), 1200);
	};

	const display = secret && !shown ? maskApiKey(value) : value;

	return (
		<div className='flex flex-col gap-1.5'>
			<div className='flex items-center gap-1'>
				<Label className='text-xs text-muted-foreground'>{label}</Label>
				{copyKey && (
					<Button
						type='button'
						size='icon-sm'
						variant='ghost'
						className='size-5'
						onClick={handleCopyKey}
						title={`Copy header name (${copyKey})`}
					>
						{keyCopied ? <Check className='size-3 text-green-600' /> : <Copy className='size-3' />}
					</Button>
				)}
			</div>
			<div className={`flex ${multiline ? 'items-start' : 'items-center'} gap-1.5`}>
				{multiline ? (
					<pre className='flex-1 m-0 bg-neutral-100 border border-neutral-200 text-neutral-800 p-2 rounded-md overflow-auto text-xs font-mono'>{value}</pre>
				) : (
					<code className='flex-1 bg-neutral-100 border border-neutral-200 text-neutral-800 p-2 rounded-md overflow-auto text-xs font-mono whitespace-nowrap'>{display}</code>
				)}
				{secret && (
					<Button type='button' size='icon-sm' variant='ghost' onClick={() => setShown((s) => !s)} title={shown ? 'Hide' : 'Reveal'}>
						{shown ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
					</Button>
				)}
				<Button type='button' size='icon-sm' variant='ghost' onClick={handleCopy} title='Copy'>
					{copied ? <Check className='size-4 text-green-600' /> : <Copy className='size-4' />}
				</Button>
			</div>
		</div>
	);
};

const richpanelFormSchema = z.object({
	apiKey: z.string().min(1, 'API key is required').max(512, 'API key is too long')
});

type RichpanelFormData = z.infer<typeof richpanelFormSchema>;

const RICHPANEL_STEPS = ['API key', 'Webhooks', 'Rules', 'Reply user', 'Tags', 'Done'];

// Event-driven webhook design: TWO HTTP Targets, each fired by its own automation rule. Message
// events (customer + agent) hit the message target and drive drafting/history; close/status events
// hit the status target, which only syncs conversation status — so a status change can never race
// a message insert or trigger a draft.
const RICHPANEL_MESSAGE_RULE_EVENTS = [
	'A customer starts a new conversation',
	'After a customer sends a new message',
	'An agent sends a new message'
];
const RICHPANEL_STATUS_RULE_EVENTS = ['After a conversation is closed'];

// HTTP Target body templates (Handlebars variables resolved by Richpanel). These mirror the
// kim-ai-backend payload types (RichpanelMessageWebhookPayload / RichpanelStatusWebhookPayload) —
// keep the two in sync.
//
// `comments` is the preferred content source: `{{ticket.comments}}` interpolates as a full JSON
// array (bodies + author identity), so it is UNQUOTED. It avoids `lastMessageText`'s ~116-char
// truncation and the brand-new-ticket API-fetch race — the backend ingests the newest comment
// directly (src=ticket). `messageText` stays as a fallback for the scalar path when comments is
// empty/unavailable.
const RICHPANEL_MESSAGE_BODY_TEMPLATE = [
	'{',
	'  "event": "message",',
	'  "ticketConversationNumber": "{{ticket.conversationNo}}",',
	'  "comments": {{ticket.comments}},',
	'  "messageText": "{{ticket.lastMessageText}}",',
	'  "senderType": "{{ticket.lastMessageSenderType}}",',
	'  "channel": "{{ticket.channel}}",',
	// The destination the conversation arrived at, and the only way a Richpanel ticket can be
	// attributed to a business unit. `via` is present on every conversation in the API but was
	// never interpolated here, so the backend read undefined and every ticket resolved to no
	// brand. `via.channel` is sent too because the top-level `ticket.channel` comes back null.
	'  "via": {{ticket.via}},',
	'  "subject": "{{ticket.subject}}",',
	'  "status": "{{ticket.status}}",',
	'  "customerEmail": "{{ticket.customer.email}}",',
	'  "customerName": "{{ticket.customer.name}}",',
	'  "customerUid": "{{ticket.customer.uid}}",',
	'  "supportEmail": "{{ticket.email}}",',
	'  "ticketPageUrl": "{{ticket.pageUrl}}",',
	'  "fromEmail": "{{ticket.from}}"',
	'}'
].join('\n');
const RICHPANEL_STATUS_BODY_TEMPLATE = [
	'{',
	'  "event": "status",',
	'  "ticketConversationNumber": "{{ticket.conversationNo}}",',
	'  "status": "{{ticket.status}}",',
	'  "previousStatus": "{{ticket.previousStatus}}",',
	'  "assignee": "{{ticket.assignee}}",',
	'  "previousAssignee": "{{ticket.previousAssignee}}",',
	'  "ticketPageUrl": "{{ticket.pageUrl}}"',
	'}'
].join('\n');

// Client-side webhook secret, minted once per setup and shown in the webhook step. It is only
// persisted when the wizard finishes (via /connect), so an abandoned setup never creates a row.
const mintWebhookToken = () => {
	const bytes = new Uint8Array(24);
	crypto.getRandomValues(bytes);
	return 'whsec_' + Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
};

const RichpanelContent = ({ integration, onOpenChange }: IntegrationContentProps & { onOpenChange?: (open: boolean) => void }) => {
	const currentUser = useAtomValue(currentUserAtom);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const connected = integration.isConnected;

	const form = useForm<RichpanelFormData>({
		resolver: zodResolver(richpanelFormSchema),
		defaultValues: { apiKey: '' }
	});
	const apiKey = form.watch('apiKey');

	const [webhookToken] = useState<string>(() => (connected ? '' : mintWebhookToken()));
	const [users, setUsers] = useState<RichpanelUser[] | null>(null);
	const [isLoadingUsers, setIsLoadingUsers] = useState(false);
	const [usersError, setUsersError] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState('');
	const [tags, setTags] = useState<RichpanelTagResult[] | null>(null);
	const [isEnsuringTags, setIsEnsuringTags] = useState(false);
	const [isConnecting, setIsConnecting] = useState(false);
	const [step, setStep] = useState(0);

	const tagsDone = tags !== null;

	// Webhook ingestion runs in the jobs service now. When VITE_WEBHOOK_INGEST_BASE_URL
	// is set, the HTTP Targets point at jobs (/webhooks/richpanel/*); otherwise they
	// fall back to the legacy kim-ai-backend route (/api/webhook/richpanel/*).
	const webhookIngestBase = import.meta.env.VITE_WEBHOOK_INGEST_BASE_URL as string | undefined;
	const backendBase = import.meta.env.VITE_API_BASE_URL || 'https://api.dev.kim.cc/api';
	// The message webhook is the PRIMARY listener — same /listener route name as every other
	// helpdesk (gorgias/zendesk/zoho). Status events go to the dedicated /status route.
	const messageTargetUrl = webhookIngestBase
		? `${webhookIngestBase}/webhooks/richpanel/listener`
		: `${backendBase}/webhook/richpanel/listener`;
	const statusTargetUrl = webhookIngestBase
		? `${webhookIngestBase}/webhooks/richpanel/status`
		: `${backendBase}/webhook/richpanel/status`;
	const accountId = String(currentUser?.accountId ?? '');

	const loadUsers = async () => {
		if (!apiKey) return;
		setIsLoadingUsers(true);
		setUsersError(false);
		try {
			const result = await integrationService.listRichpanelUsers(apiKey);
			setUsers(result.users);
		} catch {
			setUsersError(true);
			toast.error('Could not reach Richpanel to load users.');
		} finally {
			setIsLoadingUsers(false);
		}
	};

	// Auto-load the agent list the first time the reply-user step (index 3) is opened.
	useEffect(() => {
		if (step === 3 && users === null && !isLoadingUsers && !usersError && apiKey) {
			void loadUsers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [step]);

	const handleEnsureTags = async () => {
		if (!apiKey) return;
		setIsEnsuringTags(true);
		try {
			const result = await integrationService.ensureRichpanelTags(apiKey);
			setTags(result.tags);
		} catch {
			toast.error('Could not reach Richpanel to create tags.');
		} finally {
			setIsEnsuringTags(false);
		}
	};

	const handleFinish = async () => {
		setIsConnecting(true);
		try {
			const tagMap = tags ? tags.reduce((acc, t) => ({ ...acc, [t.key]: t.id }), {} as Record<string, string>) : undefined;
			await integrationService.connectRichpanelIntegration({
				api_key: apiKey,
				webhook_token: webhookToken,
				agent_uuid: selectedUserId || undefined,
				tags: tagMap
			});
			await refetch();
			onOpenChange?.(false);
		} catch (err) {
			const e = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
			toast.error(e?.error ?? e?.message ?? 'Failed to connect Richpanel');
		} finally {
			setIsConnecting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	// Already connected → manage view (the row exists; the wizard is only for first-time setup).
	if (connected) {
		const savedToken = (integration.authMetaData?.webhook_token as string | undefined) ?? '';
		return (
			<div className='flex flex-col gap-5 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected Richpanel</h3>
						<p className='text-[10px] italic text-muted-foreground'>Your Richpanel workspace is linked to Kim.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'><MoreHorizontal className='size-4' /></Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<Separator />
				<div className='flex flex-col gap-3'>
					<span className='text-sm font-semibold'>Webhooks (HTTP Targets)</span>
					<p className='text-xs text-muted-foreground'>Both targets use the same two headers below.</p>
					{savedToken && (
						<CopyField label='Header · x-kim-webhook-token' value={savedToken} secret copyKey='x-kim-webhook-token' />
					)}
					<CopyField label='Header · x-kim-account-id' value={accountId} copyKey='x-kim-account-id' />
					<Separator />
					<span className='text-xs font-semibold'>Target 1 · Messages</span>
					<CopyField label='Target URL' value={messageTargetUrl} />
					<CopyField label='Request body' value={RICHPANEL_MESSAGE_BODY_TEMPLATE} multiline />
					<Separator />
					<span className='text-xs font-semibold'>Target 2 · Status</span>
					<CopyField label='Target URL' value={statusTargetUrl} />
					<CopyField label='Request body' value={RICHPANEL_STATUS_BODY_TEMPLATE} multiline />
				</div>
			</div>
		);
	}

	const stepper = (
		<div className='flex items-start justify-between px-5'>
			{RICHPANEL_STEPS.map((label, i) => {
				const state = i < step ? 'done' : i === step ? 'active' : 'todo';
				const circle =
					state === 'done'
						? 'bg-green-50 border-green-300 text-green-700'
						: state === 'active'
							? 'bg-foreground text-background border-foreground'
							: 'bg-muted text-muted-foreground border-border';
				return (
					<div key={label} className='relative flex flex-1 flex-col items-center'>
						{i > 0 && <div className={`absolute top-3.5 right-1/2 h-px w-full ${i <= step ? 'bg-green-300' : 'bg-border'}`} />}
						<button
							type='button'
							onClick={() => { if (i <= step) setStep(i); }}
							disabled={i > step}
							className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full border text-[11px] ${circle} ${i > step ? 'cursor-default' : 'cursor-pointer'}`}
						>
							{state === 'done' ? <Check className='size-3.5' /> : i + 1}
						</button>
						<span className={`mt-1 text-[10px] ${state === 'todo' ? 'text-muted-foreground' : 'text-foreground'}`}>{label}</span>
					</div>
				);
			})}
		</div>
	);

	const apiKeyStep = (
		<div className='flex flex-col gap-3'>
			<h3 className='text-sm font-semibold'>Add your API key</h3>
			<p className='text-xs text-muted-foreground'>In Richpanel, go to Settings → Integrations → API Keys and paste the key here. Nothing is saved yet — your connection is created only when you finish.</p>
			<FormField
				control={form.control}
				name='apiKey'
				render={({ field }) => (
					<FormItem>
						<FormControl>
							<Input type='password' autoComplete='new-password' placeholder='Enter your Richpanel API key' {...field} />
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	);

	const webhookStep = (
		<div className='flex flex-col gap-3'>
			<h3 className='text-sm font-semibold'>Create the two HTTP Targets</h3>
			<div className='flex gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5'>
				<Info className='size-4 shrink-0 text-amber-600 mt-0.5' />
				<p className='text-xs text-amber-700 leading-snug'>In Richpanel, go to Settings → Integrations → HTTP Target and create TWO targets — one for messages, one for status changes. Content type: JSON Template. Both use the same two headers.</p>
			</div>
			<CopyField label='Header · x-kim-webhook-token' value={webhookToken} secret copyKey='x-kim-webhook-token' />
			<CopyField label='Header · x-kim-account-id' value={accountId} copyKey='x-kim-account-id' />
			<Separator />
			<span className='text-xs font-semibold'>Target 1 · Messages (e.g. “Kim AI Messages”)</span>
			<CopyField label='Target URL' value={messageTargetUrl} />
			<CopyField label='Request body' value={RICHPANEL_MESSAGE_BODY_TEMPLATE} multiline />
			<Separator />
			<span className='text-xs font-semibold'>Target 2 · Status (e.g. “Kim AI Status”)</span>
			<CopyField label='Target URL' value={statusTargetUrl} />
			<CopyField label='Request body' value={RICHPANEL_STATUS_BODY_TEMPLATE} multiline />
		</div>
	);

	const automationStep = (
		<div className='flex flex-col gap-3'>
			<h3 className='text-sm font-semibold'>Create the two automation rules</h3>
			<p className='text-xs text-muted-foreground'>The rules are what fire the HTTP Targets. In Richpanel, go to Automation → Rules and create one rule per target:</p>
			<div className='flex flex-col gap-1.5'>
				<Label className='text-xs text-muted-foreground'>Rule 1 · “Kim AI Messages” — When</Label>
				<div className='flex flex-col gap-1'>
					{RICHPANEL_MESSAGE_RULE_EVENTS.map((e) => (
						<span key={e} className='inline-flex items-center gap-1.5 text-xs text-foreground'><Check className='size-3 text-green-600 shrink-0' />{e}</span>
					))}
				</div>
				<span className='inline-flex items-center gap-1.5 text-xs text-foreground'><Radio className='size-3.5 text-muted-foreground shrink-0' />Action: Notify HTTP targets → the Messages target</span>
			</div>
			<div className='flex flex-col gap-1.5'>
				<Label className='text-xs text-muted-foreground'>Rule 2 · “Kim AI Status” — When</Label>
				<div className='flex flex-col gap-1'>
					{RICHPANEL_STATUS_RULE_EVENTS.map((e) => (
						<span key={e} className='inline-flex items-center gap-1.5 text-xs text-foreground'><Check className='size-3 text-green-600 shrink-0' />{e}</span>
					))}
				</div>
				<span className='inline-flex items-center gap-1.5 text-xs text-foreground'><Radio className='size-3.5 text-muted-foreground shrink-0' />Action: Notify HTTP targets → the Status target</span>
			</div>
			<div className='flex gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5'>
				<Info className='size-4 shrink-0 text-amber-600 mt-0.5' />
				<p className='text-xs text-amber-700 leading-snug'>Leave conditions empty, then click “Save and set live” on BOTH rules. A paused rule will not fire.</p>
			</div>
		</div>
	);

	const replyUserStep = (
		<div className='flex flex-col gap-3'>
			<h3 className='text-sm font-semibold'>Choose the reply user</h3>
			<p className='text-xs text-muted-foreground'>Automated replies post under this Richpanel agent. Pick an existing user — many teams create a dedicated one (e.g. richpanel-bot@kim.cc) first.</p>
			{isLoadingUsers ? (
				<div className='flex items-center gap-2 text-xs text-muted-foreground'><Loader2 className='size-4 animate-spin' />Loading users…</div>
			) : usersError ? (
				<Button type='button' variant='outline' size='sm' className='self-start' onClick={loadUsers}>Retry loading users</Button>
			) : (
				<Select value={selectedUserId} onValueChange={setSelectedUserId}>
					<SelectTrigger><SelectValue placeholder='Select a Richpanel user' /></SelectTrigger>
					<SelectContent>
						{(users ?? []).map((u) => (
							<SelectItem key={u.id} value={u.id}>{u.name ?? u.email ?? u.id}{u.name && u.email ? ` · ${u.email}` : ''}</SelectItem>
						))}
					</SelectContent>
				</Select>
			)}
			{selectedUserId && (
				<div className='flex items-center gap-2 text-xs text-green-700'><UserCheck className='size-4 text-green-600' />Reply author set.</div>
			)}
		</div>
	);

	const tagsStep = (
		<div className='flex flex-col gap-3'>
			<h3 className='text-sm font-semibold'>Tags and views</h3>
			<p className='text-xs text-muted-foreground'>Kim tags every automated reply and every spam auto-close. Create the tags, then build a view for each so your team can see Kim’s work.</p>
			{tags && tags.length > 0 && (
				<div className='flex flex-wrap gap-1.5'>
					{tags.map((t) => (
						<span key={t.key} className='inline-flex items-center gap-1 rounded-full bg-green-50 text-green-700 text-xs px-2 py-1 font-mono'><Tag className='size-3' />{t.name}</span>
					))}
				</div>
			)}
			{!tagsDone && (
				<Button type='button' variant='outline' size='sm' className='self-start' onClick={handleEnsureTags} disabled={isEnsuringTags || !apiKey}>
					{isEnsuringTags ? <Loader2 className='size-4 animate-spin' /> : <Tag className='size-4' />}
					{isEnsuringTags ? 'Creating…' : 'Create tags in Richpanel'}
				</Button>
			)}
			{tagsDone && (
				<div className='flex gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5'>
					<Info className='size-4 shrink-0 text-amber-600 mt-0.5' />
					<p className='text-xs text-amber-700 leading-snug'>In Richpanel, create two Views filtered by these tags — “Kim handled” (responded-by-kim-ai) and “Kim spam-closed” (closed-by-kim-ai).</p>
				</div>
			)}
		</div>
	);

	const doneStep = (
		<div className='flex flex-col items-center gap-2 pt-2 text-center'>
			<div className='flex h-12 w-12 items-center justify-center rounded-full bg-muted text-foreground'><Radio className='size-6' /></div>
			<h3 className='text-sm font-semibold'>Ready to connect</h3>
			<p className='text-xs text-muted-foreground'>Click Finish to save your Richpanel connection.</p>
			<div className='mt-2 grid w-full grid-cols-1 gap-1.5 text-left'>
				{[
					{ label: 'API key entered', ok: !!apiKey },
					{ label: 'Reply user selected', ok: !!selectedUserId },
					{ label: 'Tags created', ok: tagsDone }
				].map((row) => (
					<div key={row.label} className='flex items-center gap-2 text-xs'>
						{row.ok ? <Check className='size-4 text-green-600' /> : <Info className='size-4 text-amber-600' />}
						<span className={row.ok ? 'text-foreground' : 'text-muted-foreground'}>{row.label}</span>
					</div>
				))}
			</div>
		</div>
	);

	const stepContent = [apiKeyStep, webhookStep, automationStep, replyUserStep, tagsStep, doneStep][step];
	const canAdvance = step === 0 ? !!apiKey : step === 3 ? !!selectedUserId : true;
	const isLast = step === RICHPANEL_STEPS.length - 1;

	return (
		<Form {...form}>
			<div className='flex flex-col h-full'>
				{stepper}
				<Separator className='mt-3' />
				<div className='flex-1 overflow-auto px-5 py-4'>{stepContent}</div>
				<DialogFooter className='px-5 pb-5 pt-4 flex-row items-center justify-between'>
					<Button type='button' variant='outline' onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}>Back</Button>
					{isLast ? (
						<Button type='button' onClick={handleFinish} disabled={isConnecting}>
							<Radio className='size-4' />
							{isConnecting ? 'Connecting…' : 'Finish'}
						</Button>
					) : (
						<Button type='button' onClick={() => setStep((s) => Math.min(RICHPANEL_STEPS.length - 1, s + 1))} disabled={!canAdvance}>Next</Button>
					)}
				</DialogFooter>
			</div>
		</Form>
	);
};
